/**
 * servicePlanSeeding.js
 * Shared utility: seeds (or revives) the next upcoming recurring job task
 * when a Service Plan is created or activated.
 *
 * Tier 2 (Service Outlook trust pass — Feb 2026):
 *   Previously the idempotency check only inspected status == 'approved'
 *   and gated on `scheduledStart >= now`. A task that had moved to
 *   `scheduled` (manual booking) or slipped into the past was invisible
 *   to the guard → the seeder created a second Visit #1 task. Fixed by
 *   inspecting all NON_TERMINAL_RECURRING_STATUSES and ignoring
 *   `scheduledStart` recency.
 *
 * Tier 3 (Reactivation Idempotency — iter-266):
 *   New regression discovered during the Programs Workspace work: when an
 *   operator deactivated a program with "Cancel future visits" and later
 *   reactivated, the cancelled task at seriesIndex=N stayed in Firestore.
 *   The Tier-2 guard ignored it (cancelled is terminal) and seeded a
 *   fresh task at the SAME seriesIndex — producing one fresh row plus N
 *   zombie cancelled rows accumulating across deactivate/reactivate cycles.
 *
 *   Fix: before seeding, scan for cancelled tasks at the target
 *   seriesIndex with `cancelReason == 'program_deactivated'`. If found,
 *   REVIVE the most recently-cancelled one (flip status → 'approved',
 *   clear cancellation fields, stamp revivedAt) instead of creating a
 *   duplicate. True idempotency: reactivation resumes the existing visit.
 *
 * No backend, recurrence engine, or Firestore cleanup is performed here —
 * this is purely a future-write guard with a soft-revive path.
 */
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  documentId,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { resolveServicePlanStep } from './resolveServicePlanStep';
import { buildItemsSnapshot } from './buildItemsSnapshot';

// Mirrors NON_TERMINAL_RECURRING_STATUSES in buildForecast.js
const NON_TERMINAL_RECURRING_STATUSES = new Set([
  'approved',
  'scheduled',
  'en_route',
  'on_site',
  'pending',
  'in_progress',
]);

/**
 * Seeds (or revives) the next upcoming recurring job task for a lead/client.
 *
 *   1. If any non-terminal recurring task already exists → no-op.
 *   2. Else if a cancelled task at the target seriesIndex was cancelled
 *      with cancelReason='program_deactivated' → revive it. No new doc.
 *   3. Else → addDoc a fresh visit at the target seriesIndex.
 *
 * @param {string}  companyId
 * @param {object}  client         - Lead/client object
 *                                   { id, service?, recurrence?, servicePlanTemplateId?,
 *                                     _resolvedStep?, _programName? }
 * @param {object}  recurrenceData - { active, interval, planLabel, totalOccurrences, startDate }
 */
export async function seedFirstRecurringJobIfNeeded(companyId, client, recurrenceData) {
  if (!recurrenceData.active) return;
  if (!companyId || !client?.id) return;

  const tasksRef = collection(db, 'companies', companyId, 'tasks');

  // Wide query: all recurring tasks for this lead. Status / staleness filtering
  // is done in JS so we are not constrained by Firestore composite indexes
  // and we can inspect every record (incl. cancelled ones for the revive path).
  const q = query(
    tasksRef,
    where('leadId', '==', client.id),
    where('isRecurring', '==', true),
  );
  const snap = await getDocs(q);
  const allRecurring = snap.docs.map((d) => ({ id: d.id, _ref: d.ref, ...d.data() }));

  // ── Guard 1: Non-terminal task already covers the work ──────────────────
  const nonTerminalExisting = allRecurring.filter(
    (t) => NON_TERMINAL_RECURRING_STATUSES.has(t.status || '')
  );
  if (nonTerminalExisting.length > 0) {
    const targetSeriesIndex = (client?.recurrence?.completedOccurrences ?? 0) + 1;
    const matchingSeries = nonTerminalExisting.filter((t) => {
      const idx = parseInt(t.seriesIndex || t.visitNumber, 10);
      return Number.isFinite(idx) && idx === targetSeriesIndex;
    });
    console.log(
      '[seedFirstRecurring] Skipped — found',
      nonTerminalExisting.length,
      'non-terminal recurring task(s) for lead',
      client.id,
      matchingSeries.length > 0
        ? `(${matchingSeries.length} already at seriesIndex=${targetSeriesIndex})`
        : `(seriesIndex=${targetSeriesIndex} not yet present, but other non-terminal work exists)`
    );
    return;
  }

  const completed   = client?.recurrence?.completedOccurrences ?? 0;
  const seriesIndex = completed + 1;
  const intervalDays = recurrenceData.interval || 14;
  const planLabel = recurrenceData.planLabel || 'Recurring service';
  const total = recurrenceData.totalOccurrences;
  const scheduledStart = recurrenceData.startDate
    ? `${recurrenceData.startDate}T09:00:00`
    : null;

  // ── Guard 2: Revive a previously-deactivated cancelled task at the same
  //             seriesIndex instead of seeding a duplicate.
  //
  // When the operator clicks Active → Inactive with "Cancel future visits",
  // the deactivate endpoint stamps each cancelled task with
  // cancelReason='program_deactivated'. If they later re-activate, we
  // resume that same task rather than orphaning it + creating a new row.
  // Pick the most-recently-cancelled match (handles repeated cycles too).
  const revivableCancelled = allRecurring
    .filter((t) => {
      if (t.status !== 'cancelled') return false;
      if (t.cancelReason !== 'program_deactivated') return false;
      const idx = parseInt(t.seriesIndex || t.visitNumber, 10);
      return Number.isFinite(idx) && idx === seriesIndex;
    })
    .sort((a, b) => {
      // Most-recent cancellation first (cancelledAt is a server Timestamp; fall
      // back to updatedAt or doc id ordering for safety in tests / mocks).
      const aT = a.cancelledAt?.toMillis?.() ?? a.updatedAt?.toMillis?.() ?? 0;
      const bT = b.cancelledAt?.toMillis?.() ?? b.updatedAt?.toMillis?.() ?? 0;
      return bT - aT;
    });

  if (revivableCancelled.length > 0) {
    const target = revivableCancelled[0];
    console.log(
      '[seedFirstRecurring] REVIVING cancelled visit',
      target.id,
      'for lead', client.id,
      '— seriesIndex:', seriesIndex,
      '— cancelReason was: program_deactivated'
    );
    await updateDoc(target._ref, {
      status: 'approved',
      cancelReason: null,
      cancelledAt: null,
      cancelledBy: null,
      revivedAt: serverTimestamp(),
      revivedBy: auth?.currentUser?.uid || null,
      updatedAt: serverTimestamp(),
    });
    return;
  }

  // ── Guard 3: No non-terminal, no revivable cancelled → seed fresh. ──────
  let servicePlanStep = client._resolvedStep || null;
  const templateId = client.servicePlanTemplateId || null;
  let programName = client._programName || null;

  // Self-resolve step + items when caller didn't hand us a pre-resolved
  // step but the lead is linked to a program. This is the fallback path
  // for callers like `activateServicePlanFromQuote` that don't have
  // access to the tenant catalog in scope.
  if (!servicePlanStep && templateId) {
    try {
      const tplSnap = await getDoc(doc(db, 'companies', companyId, 'servicePlanTemplates', templateId));
      if (tplSnap.exists()) {
        const tpl = { id: tplSnap.id, ...(tplSnap.data() || {}) };
        const resolved = resolveServicePlanStep(tpl, seriesIndex);
        if (resolved) {
          const ids = (Array.isArray(resolved.defaultItems) ? resolved.defaultItems : [])
            .map((e) => e?.itemId).filter(Boolean);
          let catalogById = {};
          if (ids.length > 0) {
            // Batched fetch — Firestore `in` supports up to 30 ids per query.
            // For MVP a single program step almost never has >30 items, but
            // we chunk defensively.
            const chunkSize = 30;
            const uniqueIds = Array.from(new Set(ids));
            for (let i = 0; i < uniqueIds.length; i += chunkSize) {
              const slice = uniqueIds.slice(i, i + chunkSize);
              try {
                const snap = await getDocs(query(
                  collection(db, 'companies', companyId, 'catalogItems'),
                  where(documentId(), 'in', slice),
                ));
                snap.docs.forEach((d) => { catalogById[d.id] = d.data(); });
              } catch (fetchErr) {
                console.warn('[seedFirstRecurring] catalog chunk fetch failed:', fetchErr);
              }
            }
          }
          servicePlanStep = {
            ...resolved,
            items: buildItemsSnapshot(catalogById, resolved.defaultItems || [], 'program_default'),
          };
          if (!programName) programName = tpl.name || null;
        }
      }
    } catch (tplErr) {
      console.warn('[seedFirstRecurring] Could not resolve step from template (non-blocking):', tplErr);
    }
  } else if (servicePlanStep && !Array.isArray(servicePlanStep.items) && Array.isArray(servicePlanStep.defaultItems)) {
    // Caller provided a step but didn't build the items snapshot. This
    // should be rare (all first-party callers now include items) but
    // we don't want to lose the intent — surface an empty snapshot so
    // downstream code doesn't crash on missing `items`.
    servicePlanStep = { ...servicePlanStep, items: [] };
  }

  if (servicePlanStep) {
    console.log(
      '[seedFirstRecurring] Using pre-resolved step for visit', seriesIndex,
      ':', servicePlanStep.label ?? '(default)'
    );
  }

  console.log(
    '[seedFirstRecurring] Creating visit #', seriesIndex,
    'for lead', client.id,
    '— planLabel:', planLabel,
    '— interval:', intervalDays, 'days',
    '— scheduledStart:', scheduledStart ?? 'unscheduled (will appear in rail)'
  );

  await addDoc(tasksRef, {
    leadId: client.id,
    title: client.service || planLabel,
    status: 'approved',
    type: 'job',
    taskCategory: 'job',
    taskType: 'visit',
    priority: 'medium',
    assignedTo: client?.recurrence?.assignedTo || null,
    scheduledStart,
    scheduledEnd: null,
    isRecurring: true,
    recurringServiceId: client.id,
    seriesIndex,
    visitNumber: seriesIndex,
    servicePlanStep: servicePlanStep || null,
    servicePlanTemplateId: templateId,
    programName: programName || null,
    notes: `Visit #${seriesIndex} — ${planLabel} (every ${intervalDays} day${intervalDays !== 1 ? 's' : ''})`
      + (total ? ` — visit 1 of ${total}` : ''),
    createdBy: auth?.currentUser?.uid || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
